import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, Mail, MapPin, MessageCircle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  trackContactAction,
  trackActivityContactFromModal,
} from "@/utils/contactTracking";

interface ContactConciergeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Optional tracking context to allow reuse of this modal on the Activities page
   * - 'concierge' (default): uses contact_concierge_* events
   * - 'activity': uses activity_contact_* events and activity_contact_modal_open
   */
  trackingContext?: "concierge" | "activity";
  /**
   * Optional callback to open Day Pass tray (for Activities page)
   * When provided and trackingContext is "activity", shows "Book Day Pass" button instead of "Book Your Stay"
   */
  onBookDayPass?: () => void;
  /** When true, only Close is shown (e.g. Book page where user is already booking) */
  hideBookButton?: boolean;
}

const ContactConciergeModal: React.FC<ContactConciergeModalProps> = ({
  isOpen,
  onClose,
  trackingContext = "concierge",
  onBookDayPass,
  hideBookButton = false,
}) => {
  const { t } = useLanguage();

  // Track modal open
  useEffect(() => {
    if (isOpen && typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];

      // Clear all tracking variables first (prevents data pollution)
      // NOTE: button_text and button_location are preserved to retain context of how modal was opened
      window.dataLayer.push({
        // Clear modal variables (will be reset below)
        modal_type: undefined,
        modal_name: undefined,
        parent_modal: undefined,
        page_location: undefined,
        // Preserve button variables (retain context of triggering action)
        // button_text: undefined,     // ← Keep this!
        // button_location: undefined, // ← Keep this!
        // Clear booking variables
        room_type: undefined,
        // Clear contact action variables
        contact_type: undefined,
        contact_source: undefined,
        event_category: undefined,
        event_label: undefined,
        phone_number: undefined,
        email_address: undefined,
        whatsapp_number: undefined,
        region: undefined,
        destination: undefined,
        booking_method: undefined,
        // Clear event consultation variables
        event_type: undefined,
        consultation_method: undefined,
        service_category: undefined,
        // Clear Calendly variables
        calendly_action: undefined,
        calendly_location: undefined,
      });

      // Now push the actual event with clean data
      window.dataLayer.push({
        event:
          trackingContext === "activity"
            ? "activity_contact_modal_open"
            : "contact_concierge_modal_open",
        modal_type: "contact",
        modal_name:
          trackingContext === "activity"
            ? "activities_contact"
            : "concierge_contact",
        page_location: window.location.pathname,
      });
    }
  }, [isOpen, trackingContext]);

  const contactOptions = [
    {
      icon: Phone,
      title: t("contactModals.concierge.phoneTitle"),
      description: t("contactModals.concierge.phoneDesc"),
      contact: "+41 33 841 00 21",
      action: "tel:+41338410021",
      primary: true,
    },
    {
      icon: Mail,
      title: t("contactModals.concierge.emailTitle"),
      description: t("contactModals.concierge.emailDesc"),
      contact: "info@beatenberg-resortspa.ch",
      action: "mailto:info@beatenberg-resortspa.ch",
      primary: false,
    },
    {
      icon: MapPin,
      title: t("contactModals.concierge.locationTitle"),
      description: t("contactModals.concierge.locationDesc"),
      contact: "Beatenberg, Switzerland",
      action:
        "https://www.google.com/maps/place/Parkhotel+Beatenberg/@46.6976742,7.7919849,17z/data=!3m1!4b1!4m9!3m8!1s0x478fa541a569a32d:0x786359f5bdf1a5e4!5m2!4m1!1i2!8m2!3d46.6976705!4d7.7945598!16s%2Fg%2F1tkc5ln2?entry=ttu&g_ep=EgoyMDI1MDkwOS4wIKXMDSoASAFQAw%3D%3D",
      primary: false,
    },
    {
      icon: MessageCircle,
      title: t("contactModals.concierge.whatsappTitle"),
      description: t("contactModals.concierge.whatsappDesc"),
      contact: "Europe: +41 76 622 01 22 | Asia: +41 79 696 86 08",
      action: "https://wa.me/41766220122",
      primary: false,
    },
  ];

  // Skip portal during SSR
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <AnimatePresence data-oid="4y00k-0">
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000]"
            style={{ zIndex: 10000 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-oid="43f0bx8"
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
            style={{ zIndex: 10001 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-oid="8f-4._f"
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              data-oid="61pc-ih"
            >
              {/* Header */}
              <div
                className="relative bg-gradient-to-r from-[#A38648] to-[#A38648]/80 text-white p-6 rounded-t-2xl"
                data-oid="lld-9hb"
              >
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                  data-oid="mo43l.6"
                >
                  <X size={20} data-oid="yd4wm16" />
                </button>

                <div className="flex items-center gap-3" data-oid=".ewnz79">
                  <div
                    className="p-3 bg-white/20 rounded-full"
                    data-oid="g:qjtnt"
                  >
                    <MessageCircle size={24} data-oid="92w.w64" />
                  </div>
                  <div data-oid="fz3wx03">
                    <h2
                      className="text-2xl font-playfair font-bold"
                      data-oid="nz9brp1"
                    >
                      {t("contactModals.concierge.title")}
                    </h2>
                    <p className="text-white/90 font-lato" data-oid="cz-qd-l">
                      {t("contactModals.concierge.subtitle")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-oid="xvyt-ia">
                <div
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
                  data-oid="mpvr1-6"
                >
                  {contactOptions.map((option, index) => {
                    const IconComponent = option.icon;
                    return (
                      <motion.div
                        key={option.title}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg h-full ${
                          option.primary
                            ? "border-[#A38648] bg-[#A38648]/5"
                            : "border-gray-200 hover:border-[#A38648]/50"
                        }`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        data-oid="mkns9.f"
                      >
                        <div
                          className="flex items-start gap-3"
                          data-oid="_skoqr0"
                        >
                          <div
                            className={`p-2 rounded-lg flex-shrink-0 ${
                              option.primary
                                ? "bg-[#A38648] text-white"
                                : "bg-gray-100 text-[#A38648]"
                            }`}
                            data-oid="t01ea.q"
                          >
                            <IconComponent size={20} data-oid="sod00.n" />
                          </div>
                          <div className="flex-1" data-oid="1n_300c">
                            <h3
                              className="font-semibold text-gray-900 mb-1"
                              data-oid="u3m6yeu"
                            >
                              {option.title}
                            </h3>
                            <p
                              className="text-sm text-gray-600 mb-2"
                              data-oid="q__madh"
                            >
                              {option.description}
                            </p>
                            {option.title === "WhatsApp" ? (
                              <div className="space-y-2" data-oid="fhi:hpt">
                                <a
                                  href="https://wa.me/41766220122"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => {
                                    if (trackingContext === "activity") {
                                      trackActivityContactFromModal("whatsapp", {
                                        region: "europe",
                                        whatsapp_number: "+41766220122",
                                        contact_source: "activities_faq_modal",
                                      });
                                    } else {
                                      trackContactAction("concierge", "whatsapp", {
                                        region: "europe",
                                        whatsapp_number: "+41766220122",
                                      });
                                    }
                                  }}
                                  className="contact-action-track block px-3 py-2 rounded-full text-xs font-medium bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
                                  data-oid="4-p:ezq"
                                >
                                  Europe: +41 76 622 01 22
                                </a>
                                <a
                                  href="https://wa.me/41796968608"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => {
                                    if (trackingContext === "activity") {
                                      trackActivityContactFromModal("whatsapp", {
                                        region: "asia",
                                        whatsapp_number: "+41796968608",
                                        contact_source: "activities_faq_modal",
                                      });
                                    } else {
                                      trackContactAction("concierge", "whatsapp", {
                                        region: "asia",
                                        whatsapp_number: "+41796968608",
                                      });
                                    }
                                  }}
                                  className="contact-action-track block px-3 py-2 rounded-full text-xs font-medium bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
                                  data-oid="yqoultt"
                                >
                                  Asia: +41 79 696 86 08
                                </a>
                              </div>
                            ) : option.action ? (
                              <a
                                href={option.action}
                                target={
                                  option.action.startsWith("http")
                                    ? "_blank"
                                    : undefined
                                }
                                rel={
                                  option.action.startsWith("http")
                                    ? "noopener noreferrer"
                                    : undefined
                                }
                                onClick={() => {
                                  const actionType = option.action.startsWith(
                                    "tel:",
                                  )
                                    ? "phone"
                                    : option.action.startsWith("mailto:")
                                      ? "email"
                                      : option.action.includes("maps")
                                        ? "location"
                                        : "phone";
                                  const details: Record<string, any> = {};
                                  if (actionType === "phone") {
                                    details.phone_number = "+41338410021";
                                  }
                                  if (actionType === "email") {
                                    details.email_address =
                                      "info@beatenberg-resortspa.ch";
                                  }
                                  if (actionType === "location") {
                                    // Use a human-readable identifier for GA
                                    details.location = "parkhotel_beatenberg";
                                  }

                                  if (trackingContext === "activity") {
                                    trackActivityContactFromModal(
                                      actionType as
                                        | "phone"
                                        | "email"
                                        | "whatsapp"
                                        | "location",
                                      {
                                        ...details,
                                        contact_source: "activities_faq_modal",
                                      },
                                    );
                                  } else {
                                    trackContactAction(
                                      "concierge",
                                      actionType,
                                      details,
                                    );
                                  }
                                }}
                                className={`contact-action-track inline-block px-3 py-2 rounded-full text-xs font-medium transition-all duration-300 transform hover:scale-105 whitespace-nowrap ${
                                  option.primary
                                    ? "bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                                data-oid="z4eksi5"
                              >
                                {option.contact}
                              </a>
                            ) : (
                              <span
                                className="inline-block px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 whitespace-nowrap"
                                data-oid="vhkdn19"
                              >
                                {option.contact}
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Additional Info */}
                <div
                  className="bg-gradient-to-r from-pebble-beige to-pebble-beige/50 rounded-xl p-4 mb-6"
                  data-oid="sunaex3"
                >
                  <h4
                    className="font-semibold text-gray-900 mb-2"
                    data-oid="o5ecirv"
                  >
                    {t("contactModals.concierge.servicesTitle")}
                  </h4>
                  <ul
                    className="text-sm text-gray-700 space-y-1"
                    data-oid="d7frg93"
                  >
                    {t("contactModals.concierge.servicesList").map(
                      (service: string, index: number) => (
                        <li key={index} data-oid="2c2se-z">
                          • {service}
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                {/* Quick Actions */}
                <div
                  className="flex flex-col sm:flex-row gap-3"
                  data-oid="i0h:yfb"
                >
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className={hideBookButton ? "w-full" : "flex-1"}
                    data-oid="dcvvyrf"
                  >
                    {t("contactModals.concierge.closeButton")}
                  </Button>
                  {/* Show "Book Day Pass" button for Activities page, "Book Your Stay" for others */}
                  {!hideBookButton &&
                    (trackingContext === "activity" && onBookDayPass ? (
                      <Button
                        onClick={() => {
                          onBookDayPass();
                          onClose();
                        }}
                        className="flex-1 bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                        trackingType="none"
                        data-oid="book-day-pass-btn"
                      >
                        <Calendar size={20} className="mr-2" />
                        {t("activities.modal.bookDayPass")}
                      </Button>
                    ) : (
                      <a
                        href={import.meta.env.VITE_BOOKING_URL}
                        className="flex-1"
                        data-oid="rznv2gk"
                      >
                        <Button
                          trackingType="booking"
                          trackingLabel="Book Your Stay"
                          trackingLocation="concierge_modal"
                          className="w-full bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                          data-oid="jkhee2v"
                        >
                          {t("contactModals.concierge.bookButton")}
                        </Button>
                      </a>
                    ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ContactConciergeModal;
